// Admin API: auth, stats, workspaces, impersonation, system logs, health

import { adminApiRequest, clearAdminToken } from './client';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  createdAt: string;
}

// Admin Auth API
export const adminAuthApi = {
  login: (data: { email: string; password: string }) =>
    adminApiRequest<{ token: string; admin: AdminUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  me: () => adminApiRequest<{ admin: AdminUser }>('/auth/me'),

  /** Redeem the Google callback's one-time code with this tab's PKCE verifier. */
  exchangeGoogleCode: (data: { code: string; verifier: string }) =>
    adminApiRequest<{ token: string }>('/auth/google/exchange', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  logout: () => {
    clearAdminToken();
  }
};

// Admin Stats API
export const adminStatsApi = {
  getGlobalStats: () =>
    adminApiRequest<{
      workspaces: { total: number; active: number; newLast30Days: number };
      users: { total: number };
      orders: { total: number; totalRevenue: number; approximateMRR: number };
      flows: { active: number };
      customers: { total: number };
      generatedAt: string;
    }>('/stats')
};

interface Workspace {
  id: string;
  name: string;
  slug: string;
  planType: string;
  aiCredits: number;
  createdAt: string;
  updatedAt: string;
}

interface Order {
  id: string;
  customerId: string;
  total: number;
  status: string;
  createdAt: string;
}

interface ImpersonatedUser {
  id: string;
  email: string;
  name: string;
}

// Admin Workspaces API
export const adminWorkspacesApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.search) query.append('search', params.search);

    const queryString = query.toString();
    return adminApiRequest<{
      workspaces: Workspace[];
      pagination: {
        page: number;
        limit: number;
        totalCount: number;
        totalPages: number;
      };
    }>(`/workspaces${queryString ? `?${queryString}` : ''}`);
  },

  get: (id: string) =>
    adminApiRequest<{
      workspace: Workspace;
      recentOrders: Order[];
      totalRevenue: number;
    }>(`/workspaces/${id}`),

  impersonate: (workspaceId: string, userId: string, reason: string) =>
    adminApiRequest<{
      success: boolean;
      token: string;
      user: ImpersonatedUser;
      impersonationLogId: string;
      expiresIn: string;
      warning: string;
    }>(`/workspaces/${workspaceId}/impersonate`, {
      method: 'POST',
      body: JSON.stringify({ userId, reason })
    })
};

interface ImpersonationAction {
  action: string;
  timestamp: string;
  details?: string;
}

interface ImpersonationLog {
  id: string;
  adminId: string;
  workspaceId: string;
  userId: string;
  reason: string;
  startedAt: string;
  endedAt?: string;
  actionsPerformed: ImpersonationAction[];
}

// Admin Impersonation API
export const adminImpersonationApi = {
  end: (logId: string, actionsPerformed?: ImpersonationAction[]) =>
    adminApiRequest<{ success: boolean }>(`/impersonation/${logId}/end`, {
      method: 'POST',
      body: JSON.stringify({ actionsPerformed })
    }),

  logs: (params?: { page?: number; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());

    const queryString = query.toString();
    return adminApiRequest<{
      logs: ImpersonationLog[];
      pagination: {
        page: number;
        limit: number;
        totalCount: number;
        totalPages: number;
      };
    }>(`/impersonation/logs${queryString ? `?${queryString}` : ''}`);
  }
};

interface SystemLog {
  id: string;
  action: string;
  adminId: string;
  workspaceId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

// Admin System Logs API
export const adminSystemLogsApi = {
  list: (params?: { page?: number; limit?: number; action?: string; adminId?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.action) query.append('action', params.action);
    if (params?.adminId) query.append('adminId', params.adminId);

    const queryString = query.toString();
    return adminApiRequest<{
      logs: SystemLog[];
      pagination: {
        page: number;
        limit: number;
        totalCount: number;
        totalPages: number;
      };
    }>(`/system-logs${queryString ? `?${queryString}` : ''}`);
  }
};

// Admin Health API
export interface FullHealthData {
  services: Record<string, {
    status: string;
    latencyMs?: number;
    provider?: string;
    details?: string;
    error?: string;
  }>;
  server: {
    uptime: number;
    nodeVersion: string;
    memoryUsage: { heapUsed: number; heapTotal: number; rss: number };
    environment: string;
    skipWorkers: boolean;
  };
  envVars: Array<{ name: string; set: boolean; required: boolean; hint: string }>;
  queues: Array<{ name: string; waiting: number; active: number; failed: number; completed: number }>;
}

export const adminHealthApi = {
  getFull: () =>
    adminApiRequest<{ success: boolean; data: FullHealthData }>('/health/full'),
};
